import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import Button from '@mui/material/Button';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useCallback, useState } from 'react';
import { createGiveChipsOutbound } from '../../api/outbound/commonOutbounds';
import { useInstanceId } from '../../context/instance/instanceContext';
import { useSocketSend } from '../../context/webSocket/webSocketContext';
import { selectMyNumChips, selectMyNumJacks } from '../../redux/reducers/selfDataReducer';
import { useAppSelector } from '../../redux/store';
import { getCurrencyAmountString } from '../../utils/currencyUtils';
import { FormControlContainer } from '../formControlContainer';
import { OpenModalContainer } from '../openModalContainer';
import { WarningMessageContainer } from '../warningMessageContainer';
function sanitizeChipsStr(str) {
    // remove all non-digit characters
    return str.replace(/\D/g, '');
}
export const GiveChipsModal = ({ toPlayer, minChips, maxChips, taxCurrencyPercent, untaxedChipsGivenMonthly, jacksCost, onClose }) => {
    const myNumChips = useAppSelector(selectMyNumChips);
    const myNumJacks = useAppSelector(selectMyNumJacks);
    const instanceId = useInstanceId();
    const socketSend = useSocketSend();
    const [numChipsToGiveStr, setNumChipsToGiveStr] = useState('');
    const onChangeNumChips = (event) => {
        setNumChipsToGiveStr(`${sanitizeChipsStr(event.target.value)}`);
    };
    const onClickGiveChips = useCallback(() => {
        socketSend(createGiveChipsOutbound(toPlayer.id, Number(numChipsToGiveStr)));
        onClose();
    }, [numChipsToGiveStr, onClose, socketSend, toPlayer.id]);
    const hasEnoughChipsForAnyAmount = minChips <= maxChips && myNumChips !== undefined && myNumChips >= minChips;
    function getIsDisabled() {
        if (!hasEnoughChipsForAnyAmount) {
            return true;
        }
        if (!numChipsToGiveStr) {
            return true;
        }
        const numChipsToGive = Number(numChipsToGiveStr);
        if (numChipsToGive < minChips) {
            return true;
        }
        if (numChipsToGive > maxChips) {
            return true;
        }
        if (myNumChips !== undefined && myNumChips < numChipsToGive) {
            return true;
        }
        if (jacksCost > 0 && myNumJacks !== undefined && myNumJacks < jacksCost) {
            return true;
        }
        return false;
    }
    const isDisabled = getIsDisabled();
    return (_jsx(OpenModalContainer, { title: `Give Chips to ${toPlayer.name}`, onClose: onClose, maxWidth: 'sm', children: _jsxs(DialogContent, { children: [jacksCost > 0 && myNumJacks !== undefined ? (_jsx(WarningMessageContainer, { message: `Chip transfers cost ${getCurrencyAmountString(jacksCost, 'jacks', true)} each. Your Jacks: ${getCurrencyAmountString(myNumJacks, 'jacks', false)}` })) : null, taxCurrencyPercent > 0 ? (_jsxs(DialogContentText, { color: 'secondary', children: ["Monthly Chips given above ", untaxedChipsGivenMonthly.toLocaleString(), " are taxed at ", taxCurrencyPercent, "%."] })) : null, _jsx(FormControlContainer, { children: _jsx(TextField, { id: `num-chips-${instanceId}`, variant: 'filled', fullWidth: true, label: 'Number of Chips', value: numChipsToGiveStr, onChange: onChangeNumChips }) }), _jsx(DialogContentText, { children: _jsxs(Typography, { variant: 'subtitle2', children: ["Minimum: ", minChips.toLocaleString()] }) }), hasEnoughChipsForAnyAmount && (_jsxs(DialogContentText, { children: ["Range: ", minChips.toLocaleString(), " \u2014 ", maxChips.toLocaleString()] })), myNumChips !== undefined && _jsxs(DialogContentText, { children: ["You have: ", myNumChips.toLocaleString()] }), !hasEnoughChipsForAnyAmount && (_jsx(WarningMessageContainer, { message: "You don't have enough to give the minimum." })), _jsx(DialogActions, { children: _jsx(Button, { variant: 'contained', color: 'secondary', onClick: onClickGiveChips, disabled: isDisabled, children: "Give Chips" }) })] }) }));
};
